
import { 
	ScormError, 
	SCORM_VERSION, 
	SCORM_STATE,
	DataModelElement,
	IDataModelDictionary,
	IStringMap
} from './index';


const S_FALSE:string='false';
const S_TRUE :string='true';
const EMPTY_RESULT = "";
// TODO get this from somewhere else


/* Regular expressions used for validation */
const reCaseMatters = '(\\{case_matters=(true|false)\\})?';
const reCaseOrder = '((\\{case_matters=(true|false)\\}(\\{order_matters=(true|false)\\})?)|(\\{order_matters=(true|false)\\}(\\{case_matters=(true|false)\\})?))';
const reCommaDelimiter = '\\[,\\]';
const reCharacterString = '[\\u0000-\\uFFFF]';
const reLocalizedString = '((\\{lang=(\\w{1,3})(-\\S{2,8})?\\}))?([^\\{][\\u0000-\\uFFFF]{0,4000})?';
const reOrderMatters = '(\\{order_matters=(true|false)\\})?';
const rePerformanceStepAnswer = '((\\d{1,10}(\\.\\d+)?)\\[:\\](\\d{1,10}(\\.\\d+)?)|([\\u0000-\\uFFFF]{1,250}?))';
const reReal107 = '(\\d{1,10}(\\.\\d+)?)';
const reShortid = '[\\w\\.]{1,250}?';
const reNumeric = '(' + reReal107 + ')?\\[:\\](' + reReal107 + ')?';
const rePerformanceStep = '((' + reShortid + '\\[\\.\\](' + rePerformanceStepAnswer + ')?)|(' + reShortid + ')?\\[\\.\\]' + rePerformanceStepAnswer + ')';

declare global {
	interface Window {
		doSysMsg(message:string) : void;
		clearSysMsg(): void;
	}
}

/**
 * Implements the SCORM 1.2 (full) and 2004 (partial) API specifications
 */
export class SCORM_API {

	// for 1.2 & 2004 RTE required methods,
	// see  https://scorm.com/scorm-explained/technical-scorm/run-time/run-time-reference/

	public state : SCORM_STATE = SCORM_STATE.NOT_INITIALIZED;
	private dataModelDict :  IDataModelDictionary = {  };
	private dataModel : IStringMap = {};
	private lastError : ScormError = new ScormError();
	private scormVersion : SCORM_VERSION = SCORM_VERSION.SCORM_1_2;
	private allowLargeData : boolean = true; // must be overridden by featur
	private studentActivityId : string = "";
	private controlMap : {[key: string]: string} = {};
	private API_BASE = 'https://dc.digitalchalk.com/api';
	private terminateCallback : any = null;
	private debug = false;
	private commitTimerId : number = null;
	private _busy : boolean = false;
	private _busyPid : number = null;
	private _termPid : number = null;
	private _delayedTerminate : boolean = false;
	private _scormHost : string = 'scorm.digital-chalk.net';
	private _said : string;  // Student Activity ID
	private _dirtyFields : {[key: string]: string} = {};
	private _beaconFields : {[key: string] : string} = {};
	private _beaconKeys = ['cmi.core.score.raw', 'cmi.core.lesson_status'];
	private _templateVersion : Number;
	private _qready = true;
	private _dirtyq : any[] = [];
	private _windowClosing = false;
	public elementPreview : Boolean = false;
	private _sessionStartTime : Date = undefined;

	constructor(is2004? : boolean) {
		if(is2004 && is2004 === true) {
			this.scormVersion = SCORM_VERSION.SCORM_2004;
			this.log("SCORM 2004 Interface has been loaded");
		} else {
			this.scormVersion = SCORM_VERSION.SCORM_1_2;
			this.log("SCORM 1.2 Interface has been loaded");
		}
	}

	// --------------------------------------------------
	// SCORM 1.2 API Functions
	// --------------------------------------------------

    public LMSInitialize(param?:string) {
		if(this.debug) {
			this.log('LMSInitialize() was called.');
		}
		let result = S_FALSE;
		this.resetLastError();
		if(param && param.length > 0) {
			this.setLastError(201, 201, 'Cannot pass a non-empty parameter to Initialize.  Received: "' + param + '"');
		} else if(this.state == SCORM_STATE.RUNNING) {
			this.setLastError(103,101, 'Initialize was called previously');
		} else if(this.state == SCORM_STATE.TERMINATED) {
			this.setLastError(104,101, 'Initialize failed because Terminate was already called');
		} else {
			this.state = SCORM_STATE.RUNNING;
			this.log('SCORM initialized');
			result = S_TRUE;
			this._sessionStartTime = new Date();
		}
		return result;
	}

	public LMSFinish(param?:any) : string {
		if(this.debug) {
			this.log('LMSFinish() was called.');
		}
		this.resetLastError();
		let result = S_FALSE;
		if(param && param.length > 0) {
			this.setLastError(201,201,'Cannot pass a non-empty parameter to Terminate.  Received: "' + param + '"');
		} else if(this.state == SCORM_STATE.NOT_INITIALIZED) {
			this.setLastError(112, 301, 'Cannot call Terminate before Initialize');
		} else if(this.state == SCORM_STATE.TERMINATED) {
			this.setLastError(113,101,'Already Terminated. Terminate was successfully called previously.');
		} else {												
			result = S_TRUE;
		}
		if(this.debug) {
			this.log('Result ' + result + ' for LMSFinish ' + result);
		}
		if(result == S_TRUE) {
			this.commitDirty();
			this.apiTerminate();
		}		
		if(this.terminateCallback) {
			this.terminateCallback();
		}
        return result;
  }
	
	public LMSGetValue(key:string):string {
		if(this.debug) {
			this.log('LMSGetValue() was called with key "' + key + '"');
		}
		this.resetLastError();
		let result = EMPTY_RESULT;
		if(this.state == SCORM_STATE.NOT_INITIALIZED) {
			this.setLastError(122, 301, 'GetValue cannot be used before Initialize');			
		} else if(this.state == SCORM_STATE.TERMINATED) {
			this.setLastError(123, 301, 'GetValue cannot be used after Terminate');			
		}
		if(!key || key.length < 1) {
			this.setLastError(301, 201, 'Key is required for GetValue and cannot be empty');			
		}
		
		if(this.lastError.noError()) {
			let element = this.dataModelForKey(key);
			if(element == null) {
				if(this.isChildren(key)) {
					this.setLastError(301,202,'GetValue: Requested element "' + key + '" cannot have children');
				} else if(this.isCount(key)) {
					this.setLastError(301,203, 'GetValue: Requested element "' + key + '" is not an array, does not have count');
				} else if(this.startsWith(key, 'adl.data.')) {
					this.setLastError(301,201,'GetValue: Invalid data model element "' + key + '"');
				} else {
					// Element really not in the dictionary
					this.setLastError(401, 201, 'GetValue: No such data model element "' + key + '"');
				}
			} else {
				if(element.writeOnly && element.writeOnly == true) {
					this.setLastError(405,404,'Element ' + key + ' is write-only');
				} else {
					if(element.array && element.array == true) {
						result = this.getArrayValue(key, element);
					} else {
						result = this.getCommonValue(key, element);
					}
				}
			}
		}
		if(result == null) result = ""; // just in case
		return result;
	}

	public LMSSetValue(key:string, value:string) : string {
		if(this.debug) {
			this.log('LMSSetValue() was called with key "' + key + '" and value "' + value + '"');
		}
		this.resetLastError();
		let result = S_FALSE;
		if(!key || key.length < 1) {
			this.setLastError(351,351,'Key is required for SetValue and cannot be empty');
		} else if(this.state == SCORM_STATE.NOT_INITIALIZED) {
			this.setLastError(132,301, 'Cannot call SetValue before Initialize');
		} else if(this.state == SCORM_STATE.TERMINATED) {
			this.setLastError(133,133,'Cannot call SetValue after Terminate');
		} else {
			result = this.setValueImpl(key, value);
		}
		if(this.debug) {
			this.log('Result ' + result + ' for LMSSetValue ' + key + ' => ' + value)
		}
		if(result == S_TRUE) {
			this._dirtyFields[key] = value;
			if(key === 'cmi.core.score.raw' || key === 'cmi.core.lesson_status') {
				this._beaconFields[key] = value;
			}
			if(key === 'cmi.core.lesson_status') {
				if(value === 'completed' || value === 'passed' || value === 'failed') {
					this.commitDirty();
				}
			}
		}
		return result;
	}

	public LMSCommit(param:string) : string {
		if(this.debug) {
			this.log('LMSCommit() was called');
		}
		let result = S_FALSE;
		this.resetLastError();
		if(param && param.length > 0) {
			this.setLastError(201,201,'Cannot pass a non-empty parameter to Commit');
		} else if(this.state == SCORM_STATE.NOT_INITIALIZED) {
			this.setLastError(142, 301, 'Cannot Commit before Initialize');
		} else if(this.state == SCORM_STATE.TERMINATED) {
			this.setLastError(143, 301, 'Cannot Commit after Terminate');			
		} else {
			result = S_TRUE;
		}
		if(this.debug) {
			this.log('Result ' + result + ' for LMSCommit is ' + result);
		}
		if(result == S_TRUE) {
			if(this._windowClosing) {
				this.commitBeacon();
			} else {
				this.commitDirty();
			}
		}
		return result;
	}

	public LMSGetLastError() : string {
		if(this.debug) {
			this.log('LMSGetLastError called. Returning ' + this.lastError.getErrorCode(this.scormVersion));
		}
		return this.lastError.getErrorCode(this.scormVersion);
	}

	public LMSGetErrorString(code:string):string {		
		if(!code || code == "") {
			return "";
		}
		if(this.debug) {
			this.log('LMSGetLastErrorString called with code ' + code + '. Returning ' + this.lastError.getErrorString(this.scormVersion, code));
		}
		return this.lastError.getErrorString(this.scormVersion, code);
	}

	public LMSGetDiagnostic(code:string) : string {
		return this.lastError.getDiagnostic(this.scormVersion);
	}
	
	// --------------------------------------------------
	// SCORM 2004 API Functions
	// --------------------------------------------------
	
	public Initialize(param?:any) {
		this.LMSInitialize(param);
	}

	public GetValue(key:string):string {
		return this.LMSGetValue(key);
	}

	public SetValue(key:string, value:string):string {
		return this.LMSSetValue(key, value);
	}

	public Commit(emptyParam:string) : string {
		return this.LMSCommit(emptyParam);
	}

	public GetLastError():string {
		return this.LMSGetLastError();
	}

	public GetErrorString(code:string):string {
		return this.LMSGetErrorString(code);
	}

	public GetDiagnostic(code:string) : string {
		return this.LMSGetDiagnostic(code);
	}
	
	public Terminate(param?:any) : string {
		return this.LMSFinish(param);
	}

	// ------------------------------------------
	// Non-standard public functions
	// ------------------------------------------

	public DispatchInit() : IStringMap {
		var dinit : IStringMap = {};
		let startupVars = [
			'cmi.core.student_id',
            'cmi.core.student_name',
            'cmi.launch_data',
            'cmi.suspend_data',
            'cmi.core.entry', 
            'cmi.core.credit',
            'cmi.core.lesson_status'
		];
		for(var item in startupVars) {
			let element = this.dataModelForKey(startupVars[item]);
			if(element !== null) {
				dinit[startupVars[item]] = this.getCommonValue(startupVars[item], element);
			}
		};
		return dinit;
	}


	// ------------------------------------------
	// Control functions, DC specific
	// ------------------------------------------

	public makePreview() {
		this.dataModel['cmi.core.credit'] = 'no-credit';
		this.dataModel['cmi.core.lesson_mode'] = 'browse';
	}

	// ------------------------------------------
	// Commit internals
	// ------------------------------------------

	public isNotInitialized() : boolean {
		return this.state == SCORM_STATE.NOT_INITIALIZED;
	}

	public isRunning() : boolean {
		return this.state == SCORM_STATE.RUNNING;
	}

	public isTerminated() : boolean {
		return this.state == SCORM_STATE.TERMINATED;
	}

	private doTerminate() {
		this.log("Terminate AJAX started");
		let request = new XMLHttpRequest();
		request.open('get', this.API_BASE + '/scorm/terminate/' + this.studentActivityId);
		request.withCredentials = true;
		request.onreadystatechange = () => {
			if(request.readyState == 4) {
				this.state = SCORM_STATE.TERMINATED;
				if(this.debug) {
					this.log('Terminate AJAX completed with status ' + request.status);
				}
				this.doTerminateCallback();
			}
		}		
		request.send();
	}

	private pad(n:string, width:number, z?:string) : string {
		z = z || '0';
		n = n + '';
		return n.length >= width ? n : new Array(width - n.length + 1).join(z) + n;
	}

	private doubleToTimespan(secs : number) : string {
		let result : string = "";
		let rsecs : number = Math.floor(secs);
		let iHours : number = Math.floor(rsecs / 3600);
		rsecs -= (iHours * 3600);

		let iMins = Math.floor(rsecs/60);
		rsecs -= (iMins * 60);

		let msecs = (secs - Math.floor(secs)) * 100;
		// sample: 0000:00:00.00
		// sample: 03:01:39.52
		// Why, oh why, doesn't javascript have String.format or sprintf???
		if (iHours >= 100) {
			result += this.pad(iHours.toString(), 4);
		} else {
			result += this.pad(iHours.toString(), 2);
		}
		result += ':';
		result += this.pad(iMins.toString(), 2);		
		result += ':';
		result += this.pad(rsecs.toFixed(0).toString(), 2);
		if(msecs > 0) {
			result += '.' + this.pad(msecs.toFixed(0).toString(), 2);
		}		
		return result;
	}

	private timespanToDouble(timespan:string) : number {
		let result : number = 0.0;
		if(timespan) {
			try {
				let regex = new RegExp('(\\d\\d\\d?\\d?):(\\d\\d):(\\d\\d)(\\.(\\d{1,2}))?');
				let m = regex.exec(timespan);
				if(m.length > 0) {
					let iHours = parseInt(m[1]);
					let iMins = parseInt(m[2]);
					let iSecs = parseInt(m[3]);
					let iMSecs = 0;
					if(m.length > 4) {
						iMSecs = parseInt(m[5]);
					}
					result = (iHours * 60 * 60) + (iMins * 60) + iSecs + (iMSecs / 100.0);
				}
			} catch(ex) {
				result = 0.0;
				this.log("Can't convert timespan " + timespan + " to double : " + ex.message);
			}
		}
		return result;
	}

	// ------------------------------------------
	// Get/Set internals
	// ------------------------------------------

	private dataModelForKey(key:string) : DataModelElement {
		var result = null;
		if(key in this.dataModelDict) {
			result = this.dataModelDict[key];
		} else {
			let regex : RegExp = null;
			for(let elementName in this.dataModelDict) {
				if(this.dataModelDict[elementName].namePattern) {
					regex = new RegExp('^' + this.dataModelDict[elementName].namePattern + '$');
					if(regex.test(key)) {
						result = this.dataModelDict[elementName];
						break;
					}
				}
			}
		}

		return result;
	}
	

	private getCommonValue(key:string, element:DataModelElement) : string {
		let result = '';
		if(element.writeOnly && element.writeOnly == true) {
			this.setLastError(405,404,'Element ' + key + ' is write-only');
		} else if (this.endsWith(key,'._count')) {
			return this.getElementCount(key);
		} else {
			if(key in this.dataModel) {
				result = this.dataModel[key];
			} else if(element.defaultValue) {
				result = element.defaultValue;
			} else if(this.scormVersion == SCORM_VERSION.SCORM_2004 && element.setBeforeGet && element.setBeforeGet == true) {
				this.setLastError(403,0,'Data Model Element Not Initialized');
			}
		}
		return result;
	}

	private getElementCount(key:string) : string {
		let result = 0;
		var regex = new RegExp("(.*?)\\._count");
		var matches = regex.exec(key);
		if(matches && matches.length > 1) {
			let prefix = matches[1];
			let pregex = new RegExp(prefix + ".(\\d+).*");
			for(let modelKey in this.dataModel) {
				let pmat = pregex.exec(modelKey);
				if(pmat && pmat.length > 1) {
					let numkey = parseInt(pmat[1]);
					if(numkey >= result) {
						result = numkey + 1;  // zero-based indexing, so foo.0 == 1 count, foo.0,foo.1 == 2 count, etc
					}
				}
			}
		}


		return result.toString();
	}

	private getArrayValue(key:string, element:DataModelElement) : string {
		let result = '';
		let indexRegex = new RegExp(element.namePattern);
		let m = indexRegex.exec(key);
		if(m && m.length > 2) {
			if(m.length > 4) {  // array of arrays, e.g. cmi.objective.1.response.2
				let elecount1 = parseInt(this.getElementCount(m[2]+'._count'));
				let thisIndex = parseInt(m[3]);
				if(thisIndex < elecount1) {
					let elecount2 = parseInt(this.getElementCount(m[1] ));
					let index2 = parseInt(m[4]);
					if(index2 < elecount2) {
						result = this.getCommonValue(key,element);
					} else if (index2 == elecount2 && this.endsWith(key, '_count')) {
						result = this.getCommonValue(key,element);
					} else {
						this.setLastError(301,201,'Element index is out of range: ' + key);
					}
				} else {
					this.setLastError(301,201,'Element index is out of range: ' + key);
				}
			} else { // simple array, e.g. cmi.objective.1
				let elecount = parseInt(this.getElementCount(m[1] + '._count'));
				let thisIndex = parseInt(m[2]);			
				if(thisIndex < elecount) {
					result = this.getCommonValue(key,element);
				} else if(thisIndex === elecount && this.endsWith(key, '_count')) {
					result = this.getCommonValue(key,element);
				} else {
					this.setLastError(301,201,'Element index is out of range: ' + key);
				}
			}
		} else {
			// This shouldn't happen, but just in case
			this.setLastError(401,201,'Invalid data model element: ' + key);
		}
		return result;
	}

	private getReferenceElementValue(key:string, element:DataModelElement) : string {
		let result = null;
		let refPattern = element.referenceElement;
		if(refPattern) {
			let re = new RegExp('^' + element.namePattern + '$');
			let m = re.exec(key);
			if(m && m.length > 0) {
				for(let i = 0; i < m.length; i++) {
					refPattern = refPattern.replace('$' + i, m[i]);
				}
				if(refPattern in this.dataModel) {
					result = this.dataModel[refPattern];
				}
			}
		}
		return result;
	}



	private setValueImpl(key:string,value:string) : string {
		let result = S_FALSE;
		let element = this.dataModelForKey(key);
		if(!element) {
			if(this.startsWith(key, 'adl.data.')) {
				this.setLastError(351, 201, 'Invalid data model element on set ' + key);
			} else {
				this.setLastError(401, 201, 'Invalid data model element on set ' + key);
			}
		} else {
			if(this.isChildren(key) || this.isCount(key)) {
				this.setLastError(404, 402, 'Element ' + key + ' cannot be set since it is a reserved word');
			} else if(element.readOnly) {
				this.setLastError(404, 403, 'Element ' + key + ' is read-only');
			} else if(element.writeOnce && key in this.dataModel) {
				this.setLastError(351, 201, 'Element ' + key + ' can only be set once, and it has been previously set');
			} else if(element.uniqueInArray && !this.checkUnique(key,value,element)) {
				this.setLastError(351, 201, 'Element ' + key + ' must be unique value in the array');
			} else if(element.array) {
				result = this.setArrayValue(key,value,element);
			} else {
				result = this.setCommonValue(key,value,element);
			}
		}
		if(result == S_TRUE) {
			// special cases first
			if(key == 'cmi.comments') {
				if(key in this.dataModel && this.dataModel[key] != null) {
					this.dataModel[key] = this.dataModel[key] + value.toString();
				} else {
					this.dataModel[key] = value.toString();
				}
			} else if(key.match(/adl.data.\d+.store/) && value=="") {
				if (key in this.dataModel) {
					delete this.dataModel[key];
				}
			} else if(key == 'cmi.completion_status') {
				this.setCompletionStatus(value);
			} else {
				this.dataModel[key] = value.toString();
			}

			// POST
			if(key == 'cmi.progress_measure') {
				this.setCompletionStatus(null);
			}
		}
		return result;
	}

	private setCompletionStatus(value:string) {
		let threshold = 'cmi.completion.threshold' in this.dataModel ? this.dataModel['cmi.completion.threshold'] : null;
		let progress = 'cmi.progress_measure' in this.dataModel ? this.dataModel['cmi.progress_measure'] : null;
		if(threshold) {
			if(!progress) {
				// e.g. we need 50% to complete, but we don't know what % we've done
				this.dataModel['cmi.completion_status'] = 'unknown';
			} else if(parseInt(progress) < parseInt(threshold)) {
				// e.g. need 50, got 20
				this.dataModel['cmi.completion_status'] = 'incomplete';
			} else {
				// e.g. need 50, got 70
				this.dataModel['cmi.completion_status'] = 'completed';
			}
		} else if(progress) {
			// e.g we got 50% done, but how much is finished?
			this.dataModel['cmi.completion_status'] = 'unknown';
		} else {
			this.dataModel['cmi.completion_status'] = value;
		}
	}

	private setArrayValue(key:string, value:string, element:DataModelElement) : string {
		let result = S_FALSE;

		let indexRegex = new RegExp(element.namePattern);
		let m = indexRegex.exec(key);
		if(m && m.length > 2) {
			if(m.length > 4) {
				// double array match
				// m0 = full match, m1 = root2, m2 = root1, m3 = index1, m4 = index2
				let elecount1 = parseInt(this.getElementCount(m[2] + '._count'));
				let thisIndex = parseInt(m[3]);
				if(thisIndex <= elecount1) {
					let elecount2 = parseInt(this.getElementCount(m[1] + '._count'));
					let index2 = parseInt(m[4]);
					if(index2 <= elecount2) {
						if(element.prerequisite) { // SCORM 2004 only
							if(this.verifyPrerequisite(element.prerequisite, m)) {
								result = this.setCommonValue(key,value,element);
							} else {
								this.setLastError(408,201,'Data Model Element dependency not established for ' + key);
							}
						} else {
							result = this.setCommonValue(key,value,element);
						}
					} else {
						this.setLastError(351,201,'Element index is out of range for set on ' + key);	
					}
				} else {
					this.setLastError(351,201,'Element index is out of range for set on ' + key);
				}
			} else {
				// simple array match
				// m[0] is full match, m[1] is root, m[2] is the index
				let elecount = parseInt(this.getElementCount(m[1] + '._count'));
				let thisIndex = parseInt(m[2]);
				if(thisIndex <= elecount) {
					if(element.prerequisite) {
						if(this.verifyPrerequisite(element.prerequisite, m)) {
							result = this.setCommonValue(key,value,element);
						} else {
							this.setLastError(408,201,'Data Model Element dependency not established for ' + key);
						}
					} else {
						result = this.setCommonValue(key,value,element);
					}
				} else {
					this.setLastError(351,201,'Element index is out of range for set on ' + key);
				}
			}
		} else {
			// something went haywire with the regex...
			this.setLastError(401,401,'Invalid data model element ' + key);
		}

		return result;
	}

	private setCommonValue(key:string, value:string, element:DataModelElement) : string {
		let result = S_FALSE;
		if(element.dataType && !this.validateType(key,value,element)) {
			if(this.lastError.noError()) {
				this.setLastError(406,405,'Incorrect data type for ' + key);
			}
		} else if((element.min || element.max) && !this.validateNumberMinMax(key,value,element)) {
			this.setLastError(407,405,'Numeric value out of range for data element ' + key);
		} else {
			result = S_TRUE;
		}
		return result;
	}

	private validateType(key:string, value:string, element:DataModelElement):boolean {
		let result = false;
		
		// Allowed empty values don't need validation
		if(!element.notEmpty) {
			if(value && value == "") {
				result = true;
			}
		}
		if(!result) {
			if(!element.dataType) {
				result = true;  // no validation on untyped fields
			} else if(element.dataType == 'language') {
				result = this.validateTypeLanguage(value);
			} else if(element.dataType == 'longid') {
				result = this.validateTypeLongid(value);
			} else if(element.dataType == 'integer') {
				result = this.validateTypeInteger(value);
			} else if(element.dataType == 'feedback') {
				result = this.validateTypeFeedback(key, value, element);  // e.g. learner_response
			} else if(element.dataType == 'localizedstring') {
				result = this.validateTypeLocalizedString(value);
			} else if(element.dataType == 'pattern') {
				result = this.validateTypePattern(key, value, element);  // e.g. correct_responses
			} else if(element.dataType == 'real') {
				result = this.validateTypeReal(value);
			} else if(element.dataType == 'time') {
				result = this.validateTypeTime(value);
			} else if(element.dataType == 'timeinterval') {
				result = this.validateTypeTimeInterval(value);			
			} else if(element.dataType == 'CMIBlank') {
				result = this.validateTypeCMIBlank(value);
			} else if(element.dataType == 'CMIBoolean') {
				result = this.validateTypeCMIBoolean(value);
		    } else if(element.dataType == 'CMIDecimal') {
		    	result = this.validateTypeCMIDecimal(value, element);	
		    } else if(element.dataType == 'CMIFeedback') {
		    	result = this.validateTypeCMIFeedback(key, value);			
		    } else if(element.dataType == 'CMIIdentifier') {
		    	result = this.validateTypeCMIid(value, element);
		    } else if(element.dataType == 'CMISInteger') {
		    	result = this.validateTypeCMISInteger(value, element);
		    } else if(element.dataType == 'CMIString255') {
		    	result = this.validateTypeCMIString255(value);
		    } else if(element.dataType == 'CMIString4096') {
		    	result = this.validateTypeCMIString4096(value);
		    } else if(element.dataType == 'CMIString64k') {
		    	result = this.validateTypeCMIString64k(value);    	    	    	
			} else if(element.dataType == 'CMITime') {
				result = this.validateTypeCMITime(value);
			} else if(element.dataType == 'CMITimespan') {
				result = this.validateTypeCMITimespan(value);
		    } else if(element.dataType == 'CMIVocabulary' || element.dataType == 'state') {
		    	result = this.validateTypeCMIVocabulary(key, value, element);		
			} else {
				result = true;
			}
		}
		
		return result;
	}

	private validateTypeTime(value:string) : boolean {
		let result = true;
		let re = new RegExp('^(\\d{4})(-(\\d\\d)(-(\\d\\d)(T(\\d\\d)(:(\\d\\d)(:(\\d\\d)(\\.(\\d{1,2})(Z|([\\+\\-](\\d\\d)(:(\\d\\d))?))?)?)?)?)?)?)?$');
		let m = re.exec(value);
		if(m) {
			try {
				if(m[1]) {
					// YYYY 1970-2038
					let year = parseInt(m[1]);
					if(isNaN(year) || year < 1970 || year > 2038) {
						result = false;
					}
				}
				if(result && m[3]) {
					// MM 01-12
					let month = parseInt(m[3]);
					if(isNaN(month) || month < 1 || month > 12) {
						result = false;
					}
				}
				if(result && m[5]) {
					// DD 01-31, depending on month
					let day = parseInt(m[5]);
					let month = parseInt(m[3]);
					let year = parseInt(m[1]);
					if(isNaN(day) || isNaN(month) || isNaN(year) || day < 1 || day > 31) {
						result = false;
					}
					if(result) {
						if(month == 4 || month == 6 || month == 9 || month == 11) {
							// Thirty days hath September, April, June, and November
							if(day > 30) {
								result = false;
							}
						} else if(month == 2) {
							// Stupid february :)
							if(year % 4 == 0) {
								// a leap year.  don't need the other leap rules, scorm spec dictates 1970-2038
								if(day > 29) {
									result = false;
								}
							} else {
								if(day > 28) {
									result = false;
								}
							}

						}
					}
				}
				if(result && m[7]) {
					// HH 00-23
					let hours = parseInt(m[7]);
					if(isNaN(hours) || hours < 0 || hours > 23) {
						result = false;
					}
				}
				if(result && m[9]) {
					// MM 00-59
					let mins = parseInt(m[9]);
					if(isNaN(mins) || mins < 0 || mins > 59) {
						result = false;
					}
				}
				if(result && m[11]) {
					// Secs 00-59
					let secs = parseInt(m[11]);
					if(isNaN(secs) || secs < 0 || secs > 59) {
						result = false;
					}
				}
				if(result && m[13]) {
					// "fractional" secs 0-99
					let fsecs = parseInt(m[13]);
					if(isNaN(fsecs) || fsecs < 0 || fsecs > 99) {
						result = false;
					}
				}
				if(result && m[16]) {
					// HH in timezone (e.g. +05:00)
					let htz = parseInt(m[16]);
					if(isNaN(htz) || htz < 0 || htz > 23) {
						result = false;
					}
				}
				if(result && m[18]) {
					// MM in timezone
					let mtz = parseInt(m[18]);
					if(isNaN(mtz) || mtz < 0 || mtz > 59) {
						result = false;
					}
				}
				
			} catch(exc) {
				// probably a bad number
				result = false;
			}
		} else {
			result = false;
		}
		return result;
	}

	private validateTypeCMIVocabulary(key:string, value:string, element:DataModelElement) : boolean {
		let result = false;
		if(element.validValues) {
			for(let validValue of element.validValues) {
				if(value == validValue) {
					// string literal
					result = true;
					break;
				}
				if(this.startsWith(validValue, '^')) {
					// found a regex
					let re = new RegExp(validValue);
					if(re.test(value)) {
						result = true;
						break;
					}
				}
			}
		}
		return result;
	}

	private validateTypeCMITimespan(value:string) : boolean {
		let result = false;
		let re = new RegExp('^(\\d{2,4}):(\\d\\d):(\\d\\d(\\.\\d{1,2})?)$');
		let m = re.exec(value);
		if(m) {
			try {
				//m1 == hours, m2 == mins, m3==secs, m4 (not used), m5 == millis (optional)
				//unlike CMITime, no sanity check on timespan (for example, minutes can be > 60)
				if(m.length > 3) {
					let hours = parseInt(m[1]);
					let mins = parseInt(m[2]);
					let secs = parseInt(m[3]);
					if(hours >= 0 && mins >=0 && secs >= 0) {
						result =  true;
					}
				}
			} catch(exc) {
				result = false;
			}
		}
		return result;
	}

	private validateTypeCMITime(value:string) : boolean {
		let result = false;
		let re = new RegExp('^(\\d{2}):(\\d\\d):(\\d\\d(\\.\\d{1,2})?)$');
		let m = re.exec(value);
		try {
			if(m) {
				if(m.length > 3) {
					let hours = parseInt(m[1]);
					let mins = parseInt(m[2]);
					let secs = parseInt(m[3]);
					if(hours >= 0 && hours < 24) {
						if(mins >= 0 && mins < 60) {
							if(secs >=0 && secs < 60) {
								result = true;
							}
						}
					}
				}
			}
		} catch(exc) {
			result = false;
		}
		return result;
	}

	private validateMaxIndex(key:string, element:DataModelElement, maxIndex:number) : boolean {
		let result = true;
		let reName = new RegExp('^' + element.namePattern + '$');
		let m = reName.exec(key);
		if(m) {
			let thisIndex = 0;
			if(m.length > 2) {
				thisIndex = parseInt(m[4]);
			} else {
				thisIndex = parseInt(m[2]);
			}
			result = (thisIndex < maxIndex)
		}
		return result;
	}

	private checkNoDuplicatesInList(value:string,delimiter:string) : boolean {
		let result = true;
		if(value) {
			let items = value.split(new RegExp(delimiter));
			if(items.length > 1) {
				for(let i = 0; i < items.length - 1; i++) {
					for(let k = i+1; k < items.length; k++) {
						if(items[i] == items[k]) {
							result = false;
							break;  // out of the k loop
						}
					}
					if(!result) {
						break;  // out of the i loop if false already found
					}
				}
			}
		}
		return result;
	}

	private checkUnique(key:string, value:string, element:DataModelElement) : boolean {
		let result = true;
		let pattern = new RegExp('^' + element.namePattern + '$');
		let m = pattern.exec(key);
		if(m && m.length > 2) {
			let firstIndex = m[3];
			pattern = new RegExp('^' + element.namePattern.replace('\\d+', firstIndex) + '$');
		}
		for(let prop in this.dataModel) {
			if(pattern.test(prop) && (prop != key)) {
				let propVal = this.dataModel[prop];
				if(propVal && (propVal == value)) {
					result = false;
					break;
				}
			}
		}
		return result;
	}

	private validateTypePattern(key:string,value:string,element:DataModelElement) : boolean {
		let result = true;
		let refValue = this.getReferenceElementValue(key,element);
		if(refValue) {
			let re = null;
			if(refValue == 'true-false') {
				if(this.validateMaxIndex(key,element,0)) {
					re = new RegExp('^((true)|(false))$');
					result = re.test(value);
				} else {
					this.setLastError(351, 101, 'Only one pattern is allowed for true-false types');
					result = false;
				}
			} else if(refValue == 'choice') {
				// Empty OK here.. see RTE 4.2.9.1
				if(this.checkNoDuplicatesInList(value, reCommaDelimiter)) {
					if(this.checkUnique(key,value,element)) {
						re = new RegExp('^' + reShortid + '(\\[,\\]' + reShortid + '){0,9}$');
						result = re.test(value);
					} else {
						this.setLastError(351, 101, 'Duplicate choice pattern is not allowed');
						result = false;
					}
				} else {
					// no dups allowed inside the value, e.g. 1[,]1
					this.setLastError(406, 101, 'Duplicate items are not allowed inside a choice list');
					result = false;
				}
			} else if(refValue == 'fill-in') {
				re = new RegExp('^' + reCaseOrder + reLocalizedString + '(\\[,\\] ' + reLocalizedString + '){0,4}$');
				result = re.test(value);
			} else if(refValue == 'long-fill-in') {
				re = new RegExp('^' + reCaseMatters + reLocalizedString + '$');
				result = re.test(value);
			} else if(refValue == 'likert') { // likert == Strongly Agree, Agree, Disagree, etc. type scale. labels might be customized
				if(this.validateMaxIndex(key,element,0)) {
					re = new RegExp('^\\S+$');
					result = re.test(value);
				} else {
					this.setLastError(351, 101, 'Only one pattern is allowed for likert type interactions');
					result = false;
				}
			} else if(refValue == 'matching') {
				re = new RegExp('^([^\\[\\]\\s])+\\[\\.\\]([^\\[\\]\\s])+(\\[,\\]([^\\[\\]\\s])+\\[\\.\\]([^\\[\\]\\s])+){0.35}$');
				result = re.test(value);
			} else if(refValue == 'performance') {
				// No docs here except it's existence, just let it pass like the old scorm api did
			} else if(refValue == 'sequencing') {
				re = new RegExp('^' + reShortid + '(\\[,\\]' + reShortid + '){0,35}$');			
				result = re.test(value);
			} else if(refValue == 'numeric') {
				if(this.validateMaxIndex(key,element,0)) {
					let steps = value.split(/\\[,\\]/);  // 1[,]2[,]3
					for(let step in steps) {
						if(!steps[step].match('^' + reNumeric + '$')) {
							result = false;  // one of the steps isn't a valid number
						}
					}
				} else {
					this.setLastError(351, 101, 'Only one pattern is allowed for numeric interaction types');
					result = false;
				}
			} else if(refValue == 'other') {
				if(this.validateMaxIndex(key,element,0)) {
					re = new RegExp('^' + reCharacterString + '{0,4000}$');
					result = re.test(value);
				} else {
					this.setLastError(351, 101, 'Only one pattern is allowed for for "other" interaction types');
					result = false;
				}
			}
		}
		return result;
	}

	private validateTypeFeedback(key:string,value:string,element:DataModelElement) : boolean {
		let result = true;
		let refValue = this.getReferenceElementValue(key, element);
		if(refValue) {
			let re = null;
			if(refValue == 'true-false') {						
				re = new RegExp('^((true)|(false))$');
				result = re.test(value);
			} else if(refValue == 'choice') {
				// Empty OK for response, but not pattern
				re = new RegExp('^(' + reShortid + '(\\[,\\]' + reShortid + '){0,9})?$');			
				result = re.test(value);
			} else if(refValue == 'fill-in') {
				re = new RegExp('^' + reLocalizedString + '(\\[,\\]' + reLocalizedString + '){0,8}$');	
				result = re.test(value);
			} else if(refValue == 'long-fill-in') {
				re = new RegExp('^' + reLocalizedString + '$');
				result = re.test(value);
			} else if(refValue == 'likert') {
				re = new RegExp('^' + reShortid + '$');
				result = re.test(value);
			} else if(refValue == 'matching') {
				re = new RegExp('^(' + reShortid + '\\[\\.\\]' + reShortid + '(\\[,\\]' + reShortid + '\\[\\.\\]' + reShortid + '){0,35})?$');
				result = re.test(value);
			} else if(refValue == 'performance') {
			
				// unimplemented in old api
				
			} else if(refValue == 'sequencing') {
				re = new RegExp('^(' + reShortid + '(\\[,\\]' + reShortid + '){0,34})?$');
				result = re.test(value);
			} else if(refValue == 'numeric') {
				re = new RegExp('^' + reReal107 + '$');
				result = re.test(value);
			} else if(refValue == 'other') {
				re = new RegExp('^' + reCharacterString + '{0,4000}$');
				result = re.test(value); 			
			} else {
				result = false;
			}
		} else {
			result = false;
		}
		return result;
	}

	private validateTypeCMIString255(value:string) : boolean {
		let re = new RegExp('^[\\x00-\\x7F]{0,255}$');
		return re.test(value);
	}

	private validateTypeCMIString4096(value:string) : boolean {
		let re = new RegExp('^[\\x00-\\x7F]{0,4096}$');
		return re.test(value);
	}

	private validateTypeCMIString64k(value:string) : boolean {
		if(!this.allowLargeData) {
			return this.validateTypeCMIString4096(value);
		} else {
			let re = new RegExp('^[\\x00-\\x7F]{0,65536}$');
			return re.test(value);
		}
	}

	private validateTypeCMIInteger(value:string, element:DataModelElement) : boolean {
		// seems to be unused, keeping for future reference
		// CMIInteger uses validateTypeCMISInteger (short int)
		let result = false;
		let re = new RegExp('^\\d+$');
		if(re.test(value)) {
			let intValue = parseInt(value);
			if(intValue >= 0 && intValue <= 65536) {
				result = true;
			}
		}	
		return result;
	}

	private validateTypeCMISInteger(value:string, element:DataModelElement) : boolean {
		// SInteger == short integer
		let result = false;
		let re = new RegExp('^\\-?\\d+$');
		if(re.test(value)) {
			let intValue = parseInt(value);
			if(intValue >= -32768 && intValue <= 32768) {
				result = true;		
			}
		}	
		return result;
	}

	private validateTypeCMIid(value:string, element:DataModelElement) : boolean {
		let re = new RegExp('^[\\w\\.\\-_~]{1,255}$');
		return re.test(value); 
	}

	private validateTypeCMIFeedback(key:string,value:string) : boolean {
		// old scorm api did this, never seemed to be a problem?
		return true;
	}

	private validateTypeCMIDecimal(value:string, element:DataModelElement) {
		
		var re = new RegExp('^\\-?\\d+(\\.\\d+)?$');	
		return re.test(value);
	}

	private validateTypeCMIBoolean(value:string) : boolean {
		let result = false;
		if(value) {
			if(value == 'true' || value == 'false') {
				result = true;  // true as in this is a valid boolean, not the actual value (which of course is true or false)
			}
		}
		return result;
	}

	private validateTypeCMIBlank(value:string) : boolean {
		return (value == "");
	}

	private validateTypeTimeInterval(value:string) : boolean {
		if(!value) {
			return false;
		} else if(this.endsWith(value,'T')) {
			// Cant indicate Time (T) without H,M, or S
			return false; 
		} else {
			let re = new RegExp('^P((\\d+)Y)?((\\d+)M)?((\\d+)D)?(T((\\d+)H)?((\\d+)M)?((\\d+(\\.\\d{1,2})?)S)?)?$');
			return re.test(value);
		}
	}

	private validateTypeReal(value:string) : boolean {
		let re = new RegExp('^([+-])?(\\d{1,10})(\\.\\d+)?$'); // See ADL RTE comment in sec 4.1.1.7
		return re.test(value);
	}

	private validateTypeLanguage(value:string) : boolean {
		let re = new RegExp('^\\w{1,3}(-[\\w-]{2,8})?$');	
		return re.test(value);
	}

	private validateTypeLongid(value:string) : boolean {		
		let pattern = new RegExp('^\\S+$');
		if(typeof(value) == 'undefined' || value == null) {
			return false;
		}	
		if(this.startsWith(value, 'urn:')) {
			// if you are going to use an URN, you have to do it correctly
			pattern = new RegExp('^urn:\\S+:\\S+$');
		} 
		return pattern.test(value);
	}
	
	private validateTypeInteger(value:string) : boolean {
		let re = new RegExp('^\\d+$');
		return re.test(value);
	}

	private validateTypeLocalizedString(value:string): boolean {
		var re = new RegExp('^' + reLocalizedString + '$');
		return re.test(value);
	}

	private validateNumberMinMax(key:string, value:string, element:DataModelElement) : boolean {
		let result = true;

		if(isNaN(parseInt(value))) {
			result = false;
		} else {
			let v = parseInt(value);
			if(element.min != null) {
				if(v < element.min) {
					result = false;
				}
			}
			if(element.max != null) {
				if(v > element.max) {
					result = false;
				}
			}
		}

		return result;
	}

	private verifyPrerequisite(key:string, m:RegExpExecArray) : boolean {
		if(m && m.length > 0) {
			for(let i = 0; i < m.length; i++) {
				if(m[i]) {
					key = key.replace('$'+i,m[i]);
				}
			}
		}
		if(key in this.dataModel && this.dataModel[key] != null) {
			return true;
		} else {
			return false;
		}
	}

	

	// ------------------------------------------
	// Data Model functions
	// ------------------------------------------
	
	// Used to load the JSON from the Ajax call in loadDataModelElements
	private modelRequestListener = (ev : ProgressEvent) => {
		this.log('Entering modelRequest Listener');
		if(ev.currentTarget && ev.currentTarget instanceof XMLHttpRequest) {
			if((<XMLHttpRequest>ev.currentTarget).readyState == 4) {
				let dataList = JSON.parse(ev.currentTarget.responseText);
				for(var d of dataList) {
					this.dataModelDict[(<DataModelElement>d).namePattern] = d;
				}
				this.log('Data model dictionary has been loaded');
			}
		}

	}

	public loadDataModelElements(modelName?:string) {
		if(!modelName) {
			modelName = 'dataModel12.json';
		}
		let request = new XMLHttpRequest();		
		request.open("get", "assets/" + modelName, true);
		request.withCredentials = true;
		request.onreadystatechange = () => {
			if(request.readyState == 4 && request.status == 200) {
				let dataList = JSON.parse(request.responseText);
				for(var d of dataList) {
					this.dataModelDict[(<DataModelElement>d).namePattern] = d;
				}
				this.log('Data model dictionary has been loaded');
			}
		}
		request.send();
	}

	public loadInitialDataModel(scoId:string, version: string, studentRegistrationId:string, studentId:string, callback: any) {
		let request = new XMLHttpRequest();
		let cbv : {[key: string]: string} = {};
		let url = this.API_BASE + '/scorm/launch/' + scoId + '/' + studentRegistrationId;
		if(version) {
			url += '?version=' + version;
		}
		if(studentId && studentId.length == 32) {
			if(version) {
				url += "&";
			} else {
				url += "?";
			}
			url += 'studentId=' + studentId;
		}
		request.open('get', url);
		request.withCredentials = true;
		request.onreadystatechange = () => {
			if(request.readyState == 4 && request.status == 200) {
				let dataMap = JSON.parse(request.responseText);
				for(var key in dataMap) {
					if(this.startsWith(key, 'control')) {
						cbv[key] = dataMap[key];
					} else {
						this.dataModel[key] = dataMap[key];
					}
				}
				this.log('Initial data model has been loaded');
				this.log(this.dataModel);
				this.controlMap = cbv;
				if('control.activity.id' in this.controlMap) {
					this.studentActivityId = this.controlMap['control.activity.id'];
				}
				if('control.scorm.2004' in this.controlMap) {
					this.scormVersion = SCORM_VERSION.SCORM_2004;
					this.loadDataModelElements('dataModel2004.json');
				} else {
					this.loadDataModelElements('dataModel12.json');
				}
				callback(cbv);
			} else if(request.readyState == 4) {
				// returned, but not successful
				cbv["control.ajax.error"] = request.status + " : " + request.statusText;
				cbv["control.ajax.status"] = "" + request.status;
				callback(cbv);
			}
		}
		request.send();
		
	}

	public launchWithCookies(path: string) {
		console.log('Launch with cookies called');
		let newloc = 'https://' + this._scormHost + '/ctgo/launch?toProtect=' + path + '&host=' + encodeURIComponent(this._scormHost) + '&error=/launch/error.html';
		(<HTMLIFrameElement>document.getElementById('scormcontent')).src = newloc;
	}

	// ------------------------------------------launch
	// Utility Functions
	// ------------------------------------------

	public setApiBase(apiBase:string) {
		this.API_BASE = apiBase;
	}

	public setDebug() {
		this.debug = true;
	}

	public setTerminateCallback(callback:any) {
		this.terminateCallback = callback;
	}

	// needed, because endsWith exists only in ES6, which isn't supported by.. I don't really have to tell you which browser do I?  (Hint: It's IE 11)
	private endsWith(haystack:string, needle:string) : boolean {
		return haystack.indexOf(needle, haystack.length - needle.length) !== -1;
	}

	// see comment on endsWith. No IE 11 support for String.startsWith ES6
	private startsWith(haystack:string, needle:string) : boolean {
		return (haystack.substr(0, needle.length) == needle);
	}

	private isChildren(key:string) : boolean {
		return this.endsWith(key, '._children');
	}

	private isCount(key:string) :boolean {
		return this.endsWith(key, '._count');
	}

	private setLastError(code2004:number, code12:number, diagnostic?:string) {
		this.lastError = new ScormError(code2004, code12, diagnostic);
	}

	private resetLastError() {
		this.lastError = new ScormError(); // == 0, No Error
	}

	private doTerminateCallback() {
		if(this.terminateCallback) {
			if(this.busy) {
				this.delayedTerminate = true;
			} else {
				if(this._termPid === null) {
					this._termPid = setTimeout(() => {this.doTerminateIfNotBusy();}, 100);
				}
			}
		}
	}

	private doTerminateIfNotBusy() {
		if(this.terminateCallback) {
			if(this.busy === false) {
				this.terminateCallback();
			} else {
				this.delayedTerminate = true;
			}
		}
	}

	public commitBeacon() {
		if(Object.keys(this._dirtyFields).length > 0) {
			let url = this.API_BASE + '/scorm/beacon/' + this.studentActivityId;
			const beaconResult  = navigator.sendBeacon(url, new Blob( [JSON.stringify(this._dirtyFields)], {type: 'application/json'}));
			if(!beaconResult) {
				console.log("Beacon failed. Reason is not given by beacon api");
			}
			this._dirtyFields = {};
		}
	}

	public commitDirty() {
		if(Object.keys(this._dirtyFields).length > 0) {
			this._dirtyq.push(this._dirtyFields);
			this._dirtyFields = {};
			this.checkSendDirty();
		}
	}

	private checkSendDirty() {
		if(this._qready === true && this._dirtyq.length > 0) {
			this._qready = false;
			this.apiCommit(this._dirtyq.shift());
		}
	}

	// Real TS getters / setters

	public get beaconFields() : any {
		return this._beaconFields;
	}

	public get busy() : boolean {
		return this._busy;
	}

	public set busy(value : boolean) {
		if(value === this._busy) {
			return;
		}
		this._busy = value;
		if(value === true) {
			if(this._busyPid) {
				clearTimeout(this._busyPid);
			}
			setTimeout(() => {if(this.busy) {this.busy = false;}}, 2000); // so we don't get stuck forever
		} else {
			if(this.delayedTerminate) {
				this.delayedTerminate = false;
				this.doTerminateCallback();
			}
		}
	}

	public get delayedTerminate() : boolean {
		return this._delayedTerminate;
	}

	public set delayedTerminate(value: boolean) {
		this._delayedTerminate = value;
	}

	public get said() : string {
		return this._said;
	}

	public set said(said:string) {
		this._said = said;
		const dataMap : {[key: string]: any} = {
			"ABE" : this.API_BASE,
			"said" : said,
			"dcUserId" : this.dataModel['cmi.core.student_id'],
			"dcUserName" : this.dataModel['cmi.core.student_name'],
			"userAgent" : navigator.userAgent
		}
		if(this._templateVersion) {
			dataMap['templateVersion'] = this._templateVersion;
		}
		this._dirtyq.push(dataMap);
		this.checkSendDirty();
	}

	public get scormHost() : string {
		return this._scormHost;
	}

	public set scormHost(value : string) {
		this._scormHost = value;
	}

	public set templateVersion(nv : Number) {
		this._templateVersion = nv;
	}

	public get templateVersion() : Number {
		return this._templateVersion;
	}

	public get windowClosing(): boolean {
		return this._windowClosing;
	}

	public set windowClosing(newValue : boolean) {
		this._windowClosing = newValue;
	}

	private apiCommit(dataMap: any) {
		if(this.elementPreview === false) {
			let url = this.API_BASE + '/scorm/commitx/' + this.studentActivityId;

			let request = new XMLHttpRequest();
			request.open('post', url);
			request.setRequestHeader('Content-Type', 'application/json');
			request.withCredentials = true;
			request.onreadystatechange = () => {
				if(request.readyState == 4) {
					if(request.status != 200) {
						//this._dirtyq.unshift(data);  // try it again
						console.log('apiCommit failed. status == ' + request.status + ' ' + request.statusText);
						try {
							window.doSysMsg('API Commit failed. Some of your data may not have been saved.');
						} catch(e) {

						}
					} else {
						try {
							//window.clearSysMsg();
							window.doSysMsg("This is a random test of the sysmsg div. I just did an apiCommit");
						
						} catch(e) {
							
						}
					}
					this._qready = true;
					this.checkSendDirty();						
				}					
			}
			request.send(JSON.stringify(dataMap));	
		}
	}

	private apiTerminate() {
		if(this.elementPreview === false) {
			let url = this.API_BASE + '/scorm/terminate/' + this.studentActivityId;
			if(navigator.sendBeacon(url)) {
				console.log('sent beacon terminate');
			} 	else {
				console.log('sendbeacon terminate failed');
			}
		}
	}

	public keepAlivePing() {
		let url = this.API_BASE + '/scorm/keepalive';

		let request = new XMLHttpRequest();
		request.open('get', url);
		request.setRequestHeader('Content-Type', 'application/json');
		request.send();
	}

	private log(message:any) {
		
		if(typeof(console) != 'undefined') {			
			console.log(message);
		}
		
	} 
}